// src/encounter/lib/to-orders-summary.ts
// Map the A/P working canvas state (DxCard[] + the order-payload side-map) → the
// DS OrdersSummary `groups` prop (PROD-1263). This is the READ/summary of the
// orders the A/P canvas produced — grouped by category for the encounter drawer's
// Orders tab. NOT the editing canvas (that's AssessmentPlanCanvas on the 'ap' step).
//
// Orders are the v2 top-level list, grouped by PlanOrder.type. Each order's label
// comes from the canvas (PlanOrder.label); the code/detail come from the structured
// payload (order-payload.ts), keyed by orderId. Medications reuse MedicationActiveRow's
// shape; the rest map to the generic OrderLineRow shape.
import type { PlanOrder } from './to-patient-assessment';
import type { OrderPayloadMap, CodedOrderPayload } from './order-payload';
import type { OrdersSummaryGroups, OrdersSummaryOrder } from '@scripsteam/scrips-signal-ds';

/** Compose a medication "sig" line from the structured payload (best-effort; '—' when bare). */
function medSig(p: Extract<CodedOrderPayload, { kind: 'medication' }> | undefined): string {
  if (!p) return '—';
  if (p.sig?.trim()) return p.sig.trim();
  const parts = [
    p.route?.displayName,
    p.frequency,
    p.duration ? `${p.duration} ${p.durationUnit ?? ''}`.trim() : undefined,
  ].filter((x): x is string => !!x && x.trim().length > 0);
  return parts.length ? parts.join(' · ') : '—';
}

/** A vaccination order row — code is the DDC vaccine code; detail shows the lot or request state. */
function vaccinationOrder(order: PlanOrder, payload: CodedOrderPayload | undefined): OrdersSummaryOrder {
  const entry = payload?.kind === 'vaccination' ? payload.entry : undefined;
  const code = entry?.vaccine?.code ?? undefined;
  const detail = entry?.lotNo
    ? `Lot ${entry.lotNo}`
    : entry?.isAdministrationRequested
      ? 'Administration requested'
      : undefined;
  return { name: order.label, ...(code ? { code } : {}), ...(detail ? { detail } : {}), status: 'draft' };
}

/** A generic (non-med) order row from the canvas order + its payload. */
function genericOrder(order: PlanOrder, payload: CodedOrderPayload | undefined): OrdersSummaryOrder {
  // Only lab/imaging/procedure carry a minimal `code`; medication/referral/vaccination don't.
  const code =
    payload && (payload.kind === 'lab' || payload.kind === 'imaging' || payload.kind === 'procedure')
      ? payload.code?.code
      : undefined;
  let detail: string | undefined;
  if (payload?.kind === 'lab' && payload.subTests.length) {
    detail = payload.subTests.map((t) => t.displayName).filter(Boolean).join(', ');
  } else if (payload && 'note' in payload && payload.note?.trim()) {
    detail = payload.note.trim();
  } else if (payload?.kind === 'referral' && payload.additionalDiagnoses?.length) {
    detail = payload.additionalDiagnoses.map((d) => d.displayName).filter(Boolean).join(', ');
  }
  return {
    name: order.label,
    ...(code ? { code } : {}),
    ...(detail ? { detail } : {}),
    status: 'draft',
  };
}

/**
 * Group the A/P orders for the drawer Orders tab. Empty groups are omitted (the DS
 * OrdersSummary hides them); a fully empty order set yields `{}` → the caller renders
 * the EmptyState. Read-only — no sign/print wiring here (that's the host's action props).
 */
export function toOrdersSummaryGroups(
  orders: PlanOrder[],
  payloads: OrderPayloadMap,
): OrdersSummaryGroups {
  const groups: OrdersSummaryGroups = {};
  const push = <K extends keyof OrdersSummaryGroups>(k: K, item: NonNullable<OrdersSummaryGroups[K]>[number]) => {
    (groups[k] ??= [] as never[]).push(item as never);
  };

  for (const order of orders) {
    const payload = payloads.get(order.id);
    switch (order.type) {
      case 'medication': {
        const med = payload?.kind === 'medication' ? payload : undefined;
        push('medications', {
          med: { name: med?.medicationCode.displayName || order.label, sig: medSig(med) },
          status: 'active',
        });
        break;
      }
      case 'lab':
        push('labs', genericOrder(order, payload));
        break;
      case 'imaging':
        push('radiology', genericOrder(order, payload));
        break;
      case 'procedure':
        push('procedures', genericOrder(order, payload));
        break;
      case 'vaccination':
        // Vaccinations round-trip as canvas orders (PROD-1227); surface them in the drawer too.
        push('vaccinations', vaccinationOrder(order, payload));
        break;
      case 'referral':
        push('referrals', genericOrder(order, payload));
        break;
    }
  }
  return groups;
}

/** True when no group has any order (drives the drawer's empty state). */
export function isOrdersSummaryEmpty(groups: OrdersSummaryGroups): boolean {
  return Object.values(groups).every((g) => !g || g.length === 0);
}
